import { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { rtdb, MARKET_PATHS, getDateRange, logMarketData, getSportKey } from '../../rtdb/rtdbConfig';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';

const calculateGameConfidence = (confidenceStats) => {
  // If we have numerical confidence values, use them for more accurate calculation
  if (confidenceStats.numericCount && confidenceStats.numericCount > 0) {
    const avg = confidenceStats.numericTotal / confidenceStats.numericCount;
    return {
      text: avg >= 75 ? 'HIGH' : avg >= 40 ? 'MED' : 'LOW',
      numeric: Math.round(avg * 10) / 10 // Round to 1 decimal place
    };
  }

  // Fallback to text-based calculation
  const weights = { HIGH: 3, MED: 2, LOW: 1 };
  const total = (confidenceStats.HIGH || 0) * weights.HIGH +
                (confidenceStats.MED || 0) * weights.MED +
                (confidenceStats.LOW || 0) * weights.LOW;
  const count = (confidenceStats.HIGH || 0) + (confidenceStats.MED || 0) + (confidenceStats.LOW || 0);

  if (count === 0) return { text: 'NEUTRAL', numeric: 50 };
  const avg = total / count;
  const textConfidence = avg >= 2.5 ? 'HIGH' : avg >= 1.5 ? 'MED' : 'LOW';
  // Estimate numeric from text weights
  const estimatedNumeric = avg >= 2.5 ? 75 : avg >= 1.5 ? 50 : 25;
  return { text: textConfidence, numeric: estimatedNumeric };
};

// formatDateKey now imported from rtdbConfig

const useMarketDataRTDB = (timeRange = '24h', sport = 'ALL') => {
  const [marketData, setMarketData] = useState({
    stats: {
      totalPredictions: 0,
      activeUsers: 0,
      totalGames: 0,
      avgConfidence: 0,
      avgConfidenceNumeric: 0,
      totalStarCoins: 0
    },
    trendingTeams: [],
    hotGames: [],
    popularPicks: [],
    activityData: [],
    confidenceTimeSeries: [],
    gameMarkets: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Calculate date range based on timeRange
        let dates = [];
        let daysToFetch = 1;

        if (timeRange === '1h') {
          // For 1 hour, just fetch today's data
          daysToFetch = 1;
        } else if (timeRange === '24h') {
          daysToFetch = 2; // Today and yesterday
        } else if (timeRange === '3d') {
          daysToFetch = 3;
        } else if (timeRange === '7d') {
          daysToFetch = 7;
        } else if (timeRange === '14d') {
          daysToFetch = 14;
        } else if (timeRange === '30d') {
          daysToFetch = 30;
        }

        // Use getDateRange helper from rtdbConfig
        dates = getDateRange(daysToFetch);

        logMarketData('fetch', 'market-games', { dates, sport, timeRange });

        // Determine which sports to fetch
        const sportsToFetch = sport === 'ALL'
          ? ['football', 'nfl', 'nba', 'mlb', 'mma']
          : [getSportKey(sport)];

        const allGameMarkets = [];
        const seenGameIds = new Set(); // Track games we've already processed
        const teamCounts = {};
        const gameCounts = {};
        const timeSeriesMap = {};
        let totalPredictions = 0;
        let totalGames = 0;
        let totalConfidenceSum = 0;
        let totalConfidenceCount = 0;

        // Fetch data for each sport and date combination
        for (const sportKey of sportsToFetch) {
          for (const dateKey of dates) {
            const gamesRef = ref(rtdb, `${MARKET_PATHS.MARKET_GAMES}/${sportKey}/${dateKey}`);

            try {
              const snapshot = await get(gamesRef);

              if (snapshot.exists()) {
                const gamesData = snapshot.val();

                Object.entries(gamesData).forEach(([gameId, gameData]) => {
                  // Skip if we've already processed this game (deduplication)
                  const uniqueGameKey = `${sportKey}_${gameId}`;
                  if (seenGameIds.has(uniqueGameKey)) {
                    console.log(`Skipping duplicate game: ${uniqueGameKey} from date ${dateKey}`);
                    return;
                  }

                  // Filter by time range for 1h option
                  if (timeRange === '1h') {
                    const lastPrediction = gameData.lastPrediction;
                    const oneHourAgo = Date.now() - (60 * 60 * 1000);
                    if (!lastPrediction || lastPrediction < oneHourAgo) {
                      return; // Skip games with no recent activity
                    }
                  }

                  // Mark this game as seen
                  seenGameIds.add(uniqueGameKey);

                  totalGames++;
                  totalPredictions += gameData.totalPredictions || 0;

                  // Calculate confidence for this game
                  const gameConfidence = calculateGameConfidence(gameData.confidenceStats || {});

                  if (gameConfidence.numeric) {
                    totalConfidenceSum += gameConfidence.numeric;
                    totalConfidenceCount++;
                  }

                  // Get opening confidence from stored value or prediction history
                  let openingConfidence = gameData.confidenceStats?.opening || null;

                  // Convert predictionHistory object to array for time-series chart
                  let predictionHistoryArray = [];
                  if (gameData.predictionHistory) {
                    predictionHistoryArray = Object.values(gameData.predictionHistory)
                      .sort((a, b) => a.timestamp - b.timestamp)
                      .map(pred => ({
                        winner: pred.winner,
                        confidence: pred.confidence,
                        confidenceNumeric: pred.confidenceNumeric,
                        timestamp: pred.timestamp
                      }));

                    // If opening confidence wasn't stored, get it from first prediction
                    if (!openingConfidence && predictionHistoryArray.length > 0) {
                      openingConfidence = predictionHistoryArray[0].confidenceNumeric;
                    }
                  }
                  // No predictionHistory means old data - chart won't show (which is correct)

                  // Track team predictions
                  if (gameData.predictions) {
                    Object.entries(gameData.predictions).forEach(([team, count]) => {
                      teamCounts[team] = (teamCounts[team] || 0) + count;
                    });
                  }

                  // Get actual team names from the data
                  const homeTeam = gameData.teams?.home || 'TBD';
                  const awayTeam = gameData.teams?.away || 'TBD';

                  // Determine consensus and count picks for each team
                  let consensus = null;
                  let maxPicks = 0;
                  let homePicks = 0;
                  let awayPicks = 0;
                  let homeKey = null;
                  let awayKey = null;

                  if (gameData.predictions) {
                    // Helper function to match team names (handles partial matches like "Eagles" vs "Philadelphia Eagles")
                    const findTeamInPredictions = (teamName) => {
                      // First try exact match
                      if (gameData.predictions[teamName]) {
                        return teamName;
                      }

                      // Try partial match - check if any prediction key is contained in team name or vice versa
                      const teamNameLower = teamName.toLowerCase();
                      for (const predTeam of Object.keys(gameData.predictions)) {
                        const predTeamLower = predTeam.toLowerCase();
                        // Check if prediction team name is in full team name (e.g., "eagles" in "philadelphia eagles")
                        // OR if full team name is in prediction (edge case)
                        if (teamNameLower.includes(predTeamLower) || predTeamLower.includes(teamNameLower)) {
                          return predTeam;
                        }
                      }
                      return null;
                    };

                    // Find matching prediction keys for home and away teams
                    homeKey = findTeamInPredictions(homeTeam);
                    awayKey = findTeamInPredictions(awayTeam);

                    // Debug logging for NFL
                    if (sportKey === 'nfl' && gameId === '17392') {
                      console.log('🔍 Team Matching Debug:', {
                        homeTeam,
                        awayTeam,
                        homeKey,
                        awayKey,
                        predictions: gameData.predictions,
                        predictionKeys: Object.keys(gameData.predictions)
                      });
                    }

                    // Count picks using matched keys
                    homePicks = homeKey ? (gameData.predictions[homeKey] || 0) : 0;
                    awayPicks = awayKey ? (gameData.predictions[awayKey] || 0) : 0;

                    // Find the team with most predictions
                    Object.entries(gameData.predictions).forEach(([team, count]) => {
                      if (count > maxPicks) {
                        maxPicks = count;
                        consensus = team;
                      }
                    });
                  }

                  const totalPicks = homePicks + awayPicks;

                  // Calculate percentages based on average confidence for the consensus pick
                  let homePercentage, awayPercentage;

                  // Get average confidence from aggregated stats
                  let avgConfidence = 50; // Default to 50/50
                  if (gameData.confidenceStats?.numericCount > 0) {
                    avgConfidence = gameData.confidenceStats.numericTotal / gameData.confidenceStats.numericCount;
                    // Clamp between reasonable bounds
                    avgConfidence = Math.min(95, Math.max(5, avgConfidence));
                  }

                  // Assign confidence percentages
                  // The bars should show confidence in WINNING, not just position
                  if (!consensus || totalPicks === 0) {
                    // No predictions yet - default to 50/50
                    homePercentage = 50;
                    awayPercentage = 50;
                  } else if (consensus === homeKey || consensus === homeTeam) {
                    // Home team is favored - home gets high confidence
                    // Check both the matched key (e.g., "Giants") and full name (e.g., "New York Giants")
                    homePercentage = Math.round(avgConfidence);
                    awayPercentage = 100 - homePercentage;
                  } else if (consensus === awayKey || consensus === awayTeam) {
                    // Away team is favored - away gets high confidence
                    // Check both the matched key (e.g., "Eagles") and full name (e.g., "Philadelphia Eagles")
                    homePercentage = 100 - Math.round(avgConfidence);
                    awayPercentage = Math.round(avgConfidence);
                  } else {
                    // Fallback to 50/50 if consensus doesn't match teams
                    homePercentage = 50;
                    awayPercentage = 50;
                  }

                  // Debug logging AFTER percentage calculation
                  if (sportKey === 'nfl' && gameId === '17392') {
                    console.log('🔍 NFL Percentage Calculation Debug:', {
                      gameId,
                      consensus,
                      homeKey,
                      awayKey,
                      homeTeam,
                      awayTeam,
                      avgConfidence,
                      'consensus === homeKey': consensus === homeKey,
                      'consensus === homeTeam': consensus === homeTeam,
                      'consensus === awayKey': consensus === awayKey,
                      'consensus === awayTeam': consensus === awayTeam,
                      homePercentage,
                      awayPercentage,
                      totalPicks,
                      homePicks,
                      awayPicks
                    });
                  }

                  // Determine market strength based on consensus percentage
                  let marketStrength = 'weak';
                  if (totalPicks > 0) {
                    const consensusPercentage = (maxPicks / totalPicks) * 100;
                    if (consensusPercentage >= 70) marketStrength = 'strong';
                    else if (consensusPercentage >= 55) marketStrength = 'moderate';
                  }

                  // Determine market movement based on opening vs current confidence
                  let marketMovement = 'stable';
                  if (openingConfidence && gameConfidence.numeric) {
                    const confidenceChange = gameConfidence.numeric - openingConfidence;
                    if (confidenceChange > 5) marketMovement = 'rising';
                    else if (confidenceChange < -5) marketMovement = 'falling';
                  }

                  // Create game market entry with actual team names

                  const gameMarket = {
                    gameId: gameId,
                    sport: sportKey.toUpperCase(),
                    dateKey: dateKey,
                    gameTimestamp: gameData.gameTimestamp || null,
                    lastPredictionTime: gameData.lastPrediction || null,
                    firstPredictionTime: gameData.firstPrediction || null, // Market open time
                    homePicks: homePicks,
                    awayPicks: awayPicks,
                    homePercentage: homePercentage,
                    awayPercentage: awayPercentage,
                    consensus: consensus,
                    consensusConfidence: gameConfidence.text,
                    consensusConfidenceNumeric: gameConfidence.numeric,
                    openingConfidence: openingConfidence, // Opening confidence value
                    marketStrength: marketStrength,
                    marketMovement: marketMovement, // Now calculated based on confidence change
                    totalPredictions: gameData.totalPredictions || 0,
                    totalStarCoins: gameData.totalStarCoins || 0, // Total coins entering market
                    predictions: predictionHistoryArray, // Array of individual predictions for chart
                    predictionCounts: gameData.predictions || {}, // Team vote counts
                    confidenceStats: gameData.confidenceStats || {},
                    poolStatus: gameData.poolStatus || {},
                    poolData: null, // Will be fetched separately from Firestore
                    // Use actual team names from RTDB
                    home: homeTeam,
                    away: awayTeam
                  };

                  allGameMarkets.push(gameMarket);

                  // Track for hot games
                  const matchup = `${gameMarket.home} vs ${gameMarket.away}`;
                  gameCounts[matchup] = gameData.totalPredictions || 0;

                  // Build time series data (simplified - would need prediction timestamps)
                  const hour = new Date(gameData.lastPrediction || Date.now()).getHours();
                  if (!timeSeriesMap[hour]) {
                    timeSeriesMap[hour] = {
                      timestamp: hour,
                      HIGH: 0,
                      MED: 0,
                      LOW: 0,
                      total: 0
                    };
                  }

                  if (gameData.confidenceStats) {
                    timeSeriesMap[hour].HIGH += gameData.confidenceStats.HIGH || 0;
                    timeSeriesMap[hour].MED += gameData.confidenceStats.MED || 0;
                    timeSeriesMap[hour].LOW += gameData.confidenceStats.LOW || 0;
                    timeSeriesMap[hour].total += gameData.totalPredictions || 0;
                  }
                });
              }
            } catch (err) {
              console.warn(`No data for ${sportKey}/${dateKey}:`, err);
            }
          }
        }

        // Calculate trending teams (top 10)
        const trendingTeams = Object.entries(teamCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([team, picks], index) => ({
            rank: index + 1,
            team: team,
            picks: picks,
            percentage: totalPredictions > 0 ? ((picks / totalPredictions) * 100).toFixed(1) : 0
          }));

        // Calculate hot games (top 5)
        const hotGames = Object.entries(gameCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([matchup, predictions]) => ({
            matchup: matchup,
            predictions: predictions,
            heat: predictions >= 20 ? '🔥🔥🔥' : predictions >= 10 ? '🔥🔥' : '🔥'
          }));

        // Convert time series map to array
        const confidenceTimeSeries = Object.values(timeSeriesMap)
          .sort((a, b) => a.timestamp - b.timestamp);

        // Sort game markets by recency
        allGameMarkets.sort((a, b) => (b.lastPredictionTime || 0) - (a.lastPredictionTime || 0));

        // Fetch betting pool data for each game (from Firestore)
        // Note: This is a separate collection from predictions
        const marketsWithPools = await Promise.all(
          allGameMarkets.map(async (market) => {
            try {
              // Construct pool ID (sport_gameId format)
              const poolId = `${market.sport.toLowerCase()}_${market.gameId}`;
              const poolRef = doc(db, 'bettingPools', poolId);
              const poolSnapshot = await getDoc(poolRef);

              if (poolSnapshot.exists()) {
                const poolData = poolSnapshot.data();
                return {
                  ...market,
                  poolData: {
                    homePot: poolData.homePot || 0,
                    awayPot: poolData.awayPot || 0,
                    totalBettors: poolData.totalBettors || 0,
                    status: poolData.status || 'open'
                  }
                };
              }
            } catch (error) {
              console.warn(`Failed to fetch pool data for game ${market.gameId}:`, error);
            }
            return market; // Return market without pool data if fetch fails
          })
        );

        // Calculate total pool amount from all betting pools
        let totalPoolAmount = 0;
        marketsWithPools.forEach(market => {
          if (market.poolData) {
            totalPoolAmount += (market.poolData.homePot + market.poolData.awayPot);
          }
        });

        // Calculate average confidence
        const avgConfidenceNumeric = totalConfidenceCount > 0
          ? totalConfidenceSum / totalConfidenceCount
          : 50;

        const avgConfidence = avgConfidenceNumeric >= 75 ? 'HIGH' :
                             avgConfidenceNumeric >= 40 ? 'MED' : 'LOW';

        // Update market data
        setMarketData({
          stats: {
            totalPredictions: totalPredictions,
            activeUsers: Math.floor(totalPredictions * 0.7), // Estimate
            totalGames: totalGames,
            avgConfidence: avgConfidence,
            avgConfidenceNumeric: Math.round(avgConfidenceNumeric),
            totalStarCoins: totalPredictions * 7, // 7 coins per prediction
            totalPoolAmount: totalPoolAmount // Total coins in betting pools
          },
          trendingTeams: trendingTeams,
          hotGames: hotGames,
          popularPicks: [],
          activityData: [],
          confidenceTimeSeries: confidenceTimeSeries,
          gameMarkets: marketsWithPools
        });

        logMarketData('success', 'market-games', {
          totalGames,
          totalPredictions,
          gameMarkets: allGameMarkets.length
        });

      } catch (err) {
        logMarketData('error', 'market-games', { error: err.message });
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
  }, [timeRange, sport]);

  return { marketData, loading, error };
};

export default useMarketDataRTDB;